import { ApplyOperationsAction, Operation, Section } from "../../types";
import { findBlockById } from "../block/find-block-by-id";
import { findSectionById } from "../section/find-section-by-id";
import { findSectionIndexByBlockId } from "../section/find-section-index-by-block-id";
import { findSectionIndexById } from "../section/find-section-index-by-id";

function inverseOperation(
  op: Operation,
  oldState: Section[],
  newState: Section[],
): Operation {
  if (op.type === 'create-block') {
    return {
      type: 'delete-block',
      sectionId: op.sectionId,
      blockId: op.block.id,
    };
  } else if (op.type === 'delete-block') {
    const block = findBlockById(oldState, op.blockId);
    const sectionIndex = findSectionIndexById(oldState, op.sectionId);
    if (!block || sectionIndex === null) {
      throw new Error('Block or section not found');
    }
    const blockIndex = oldState[sectionIndex].blocks.findIndex((b) => b.id === op.blockId);
    return {
      type: 'create-block',
      sectionId: op.sectionId,
      index: blockIndex,
      block,
    };
  } else if (op.type === 'move-block') {
    const oldFromSectionIndex = findSectionIndexByBlockId(oldState, op.id)!;
    const oldFromSection = oldState[oldFromSectionIndex];
    const fromBlockIndex = oldFromSection.blocks.findIndex((b) => b.id === op.id);
    return {
      type: 'move-block',
      id: op.id,
      to: {
        sectionId: oldFromSection.id,
        index: fromBlockIndex,
      },
    };
  } else if (op.type === 'delete-section') {
    const index = findSectionIndexById(oldState, op.id)!;
    return {
      type: 'create-section',
      id: op.id,
      index: index,
    };
  } else if (op.type === 'create-section') {
    return {
      type: 'delete-section',
      id: op.id,
    };
  } else if (op.type === 'move-section') {
    const oldSectionIndex = findSectionIndexById(oldState, op.id);
    if (oldSectionIndex === null || oldSectionIndex === -1) {
      throw new Error('Section not found');
    }
    return {
      type: 'move-section',
      id: op.id,
      to: {
        index: oldSectionIndex,
      },
    };
  } else if (op.type === 'set-section-name') {
    const section = findSectionById(oldState, op.id)!;
    return {
      type: 'set-section-name',
      id: op.id,
      name: section.name,
    };
  } else if (op.type === 'set-block-width') {
    const block = findBlockById(oldState, op.id)!;
    return {
      type: 'set-block-width',
      id: op.id,
      width: block.layout.spec.width,
    };
  } else if (op.type === 'set-block-content') {
    const block = findBlockById(oldState, op.id)!;
    return {
      type: 'set-block-content',
      id: op.id,
      content: block.content,
    };
  } else if (op.type === 'set-block-layout') {
    const block = findBlockById(oldState, op.id)!;
    return {
      type: 'set-block-layout',
      id: op.id,
      layout: block.layout,
    };
  } else {
    const assertNever: never = op;
    throw new Error('Unexpected operation type: ' + assertNever);
  }
}

export function inverseOperations(
  operations: Operation[],
  oldState: Section[],
  newState: Section[]
): ApplyOperationsAction {
  const inverseOperations = operations
    .map((op) => inverseOperation(op, oldState, newState))
    .reverse();

  return {
    type: 'apply-operations',
    operations: inverseOperations,
  };
}